import React from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import Stack from "@mui/material/Stack";
import { useMutation, useQuery } from "@apollo/client";
import {
  GET_INVOICE_BY_ID,
  GetInvoiceByIdData,
  GetInvoiceByIdVars,
  VOID_INVOICE,
  VoidInvoiceData,
  VoidInvoiceVars,
  RECORD_INVOICE_PAYMENT,
  RecordInvoicePaymentData,
  RecordInvoicePaymentVars,
  REVIEW_INVOICE,
  ReviewInvoiceData,
  ReviewInvoiceVars,
} from "common/graphql";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import LocalLevelError from "@app.automotus.io/components/common/LocalLevelError";
import { useSnackPackContext } from "@app.automotus.io/components/context/SnackPack";
import { InvoicesLocationState } from "@curbsuite/routes/dashboard/invoices/index";
import { InvoicePageContent } from "./InvoicePageContent";

export const Invoice: React.FC = () => {
  const { invoiceId = "" } = useParams<{ invoiceId: string }>();
  const { data, loading, error, refetch } = useQuery<GetInvoiceByIdData, GetInvoiceByIdVars>(GET_INVOICE_BY_ID, {
    variables: { invoiceId },
    onError(err) {
      console.error(`failed to fetch invoice by id ${invoiceId}`, err);
    },
  });

  const navigate = useNavigate();
  const location = useLocation();
  const { publishSnackbarMessage } = useSnackPackContext();

  const [voidInvoice, { loading: voidLoading }] = useMutation<VoidInvoiceData, VoidInvoiceVars>(VOID_INVOICE);
  const [recordInvoicePayment, { loading: recordPaymentLoading }] = useMutation<
    RecordInvoicePaymentData,
    RecordInvoicePaymentVars
  >(RECORD_INVOICE_PAYMENT);
  const [reviewInvoice, { loading: reviewLoading }] = useMutation<ReviewInvoiceData, ReviewInvoiceVars>(REVIEW_INVOICE);

  const invoice = data?.invoice;
  const showVoidButton = !!(
    invoice &&
    !invoice.voided_at &&
    !invoice.is_paid_in_full &&
    invoice.notice_integration_partner_config?.is_void_submitted_notice_allowed
  );
  const showRecordPaymentButton = !!(
    invoice &&
    !invoice.voided_at &&
    !invoice.is_paid_in_full &&
    invoice.notice_integration_partner_config?.is_apply_payment_to_notice_allowed
  );

  const handleVoidSubmit = async ({ reason, voidedAt }: { reason: string; voidedAt: Date }) => {
    try {
      await voidInvoice({
        variables: { id: invoiceId, reason, voidedAt },
      });
      publishSnackbarMessage({ severity: "success", message: "Invoice voided" });
      refetch();
    } catch (err) {
      console.error("Failed to void invoice", err);
      publishSnackbarMessage({ severity: "error", message: "Failed to void invoice" });
    }
  };

  const handleRecordPaymentSubmit = async ({ paidAmountCents, paidAt }: { paidAmountCents: number; paidAt: Date }) => {
    try {
      await recordInvoicePayment({
        variables: { id: invoiceId, paidAmount: paidAmountCents, paidAt: paidAt },
      });
      publishSnackbarMessage({ severity: "success", message: "Payment recorded" });
      refetch();
    } catch (err) {
      console.error("Failed to record payment", err);
      publishSnackbarMessage({ severity: "error", message: "Failed to record payment" });
    }
  };

  const handleIssueSubmit = async () => {
    try {
      await reviewInvoice({ variables: { invoiceId, approve: true } });
      publishSnackbarMessage({ severity: "success", message: "Invoice issued" });
      refetch();
    } catch (err) {
      console.error("Failed to issue invoice", err);
      publishSnackbarMessage({ severity: "error", message: "Failed to issue invoice" });
    }
  };

  const handlePendingVoidSubmit = async () => {
    try {
      await reviewInvoice({ variables: { invoiceId, approve: false } });
      publishSnackbarMessage({ severity: "success", message: "Invoice voided" });
      refetch();
    } catch (err) {
      console.error("Failed to void invoice", err);
      publishSnackbarMessage({ severity: "error", message: "Failed to void invoice" });
    }
  };

  const locationState = location.state as InvoicesLocationState | null;
  const backButtonLink = `..?page=${locationState?.invoiceTablePage || 0}&pageSize=${
    locationState?.invoiceTablePageSize || 31
  }${locationState?.invoiceTableSortStr ? `&sort=${encodeURIComponent(locationState?.invoiceTableSortStr)}` : ""}`;

  if (error) {
    return (
      <>
        <IconButton component={Link} to={backButtonLink}>
          <ArrowBackIosIcon />
        </IconButton>
        <Stack spacing={3} width={"100%"}>
          <Box>
            <LocalLevelError onTryAgain={refetch} />
          </Box>
        </Stack>
      </>
    );
  }

  return (
    <InvoicePageContent
      invoiceId={invoiceId}
      invoice={invoice ?? null}
      loading={loading}
      backButtonLink={backButtonLink}
      showVoidButton={showVoidButton}
      showRecordPaymentButton={showRecordPaymentButton}
      onVoidSubmit={handleVoidSubmit}
      onRecordPaymentSubmit={handleRecordPaymentSubmit}
      onIssueSubmit={handleIssueSubmit}
      onPendingVoidSubmit={handlePendingVoidSubmit}
      voidSubmitting={voidLoading}
      recordPaymentSubmitting={recordPaymentLoading}
      issueSubmitting={reviewLoading}
      pendingVoidSubmitting={reviewLoading}
      onNavigateToTransaction={(transactionId) => navigate(`transactions/${transactionId}`)}
    />
  );
};

export default Invoice;
